/**
 * CalendrierProductionEmploye — Page /mon-planning
 * - Employés terrain : Vue Gantt mensuelle de leurs chantiers (filtrée par employé)
 * - Employés admin : Calendrier avec RDV clients + événements personnels ajoutables
 */
import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, ExternalLink, Plus, Trash2, Calendar, Users, Check, X, Bell } from 'lucide-react';
import { isJourFerieCCQ, isCongeCCQ } from '@/lib/ccq-holidays';
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples
import { formatItemHoraire } from '@/lib/formatItemHoraire'; // 🆕 [v13.60.2 — 2026-08-12]

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const JOURS_HEADER = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const MOIS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const ADMIN_ROLES = ['proprietaire', 'charge_projet', 'adjoint_admin', 'admin'];

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getWeeks(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  let start = new Date(firstDay);
  while (start.getDay() !== 0) start.setDate(start.getDate() - 1);
  const weeks = [];
  let current = new Date(start);
  while (current <= lastDay || current.getDay() !== 0) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    weeks.push(week);
    if (current > lastDay && current.getDay() === 1) break;
  }
  return weeks;
}

// ======================== VUE TERRAIN (Gantt) ========================
function VueGanttTerrain({ token, user, year, month }) {
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState([]);
  const [agendaEvents, setAgendaEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const todayStr = toDateStr(new Date());

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    const employe_id = user?.employee_id || user?.id;
    Promise.all([
      axios.get(`${API_URL}/api/employees/my-schedule-month?year=${year}&month=${month + 1}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.data).catch(() => ({ schedule: [] })),
      axios.get(`${API_URL}/api/agenda-perso?employe_id=${employe_id}&year=${year}&month=${month + 1}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.data).catch(() => ({ events: [] })),
    ])
      .then(([schedData, agendaData]) => {
        setSchedule(schedData.schedule || []);
        setAgendaEvents(agendaData.events || []);
      })
      .catch(() => { setSchedule([]); setAgendaEvents([]); })
      .finally(() => setLoading(false));
  }, [token, year, month]);

  const weeks = getWeeks(year, month);

  const getEventsForWeek = (week) => {
    const weekStrs = week.map(toDateStr);
    return schedule.map(ev => {
      const dateSet = new Set(ev.dates || []);
      let firstIdx = -1, lastIdx = -1;
      weekStrs.forEach((d, i) => {
        if (dateSet.has(d)) {
          if (firstIdx === -1) firstIdx = i;
          lastIdx = i;
        }
      });
      return { ...ev, firstIdx, lastIdx };
    }).filter(ev => ev.firstIdx !== -1);
  };

  // Algorithme de packing : regroupe les événements non-chevauchants sur la même ligne
  // pour éviter les "escaliers" visuels quand plusieurs BTs courts se succèdent dans la semaine
  const packEventsIntoLanes = (events) => {
    // Tri : événements qui commencent plus tôt d'abord, puis les plus longs en premier
    const sorted = [...events].sort((a, b) => a.firstIdx - b.firstIdx || (b.lastIdx - b.firstIdx) - (a.lastIdx - a.firstIdx));
    const lanes = []; // chaque lane = array d'événements qui ne se chevauchent pas
    for (const ev of sorted) {
      let placed = false;
      for (const lane of lanes) {
        const last = lane[lane.length - 1];
        if (last.lastIdx < ev.firstIdx) {
          lane.push(ev);
          placed = true;
          break;
        }
      }
      if (!placed) lanes.push([ev]);
    }
    return lanes;
  };

  // Événements agenda (RDV/invitations acceptées) pour une semaine
  const getAgendaForWeek = (week) => {
    const weekStrs = week.map(toDateStr);
    return agendaEvents.filter(ev => weekStrs.includes(ev.date)).map(ev => {
      const dayIdx = weekStrs.indexOf(ev.date);
      return { ...ev, dayIdx };
    });
  };

  const eventColor = (ev) => {
    if (ev.event_type === 'conge') return '#d97706';
    if (ev.event_type === 'lavage') return '#06b6d4';
    if (ev.event_type === 'horaire') return '#4f46e5';
    if (ev.event_type === 'bt_interne' || ev.est_interne) return '#ea580c';
    if (ev.type_travaux === 'exterieur') return '#16a34a';
    return '#2563eb';
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-sm text-slate-400">Chargement...</div>;

  return (
    <div className="flex-1 flex flex-col overflow-x-auto">
      {/* En-tête jours */}
      <div className="grid grid-cols-7 bg-blue-500 text-white text-center shrink-0 min-w-[350px]">
        {JOURS_HEADER.map((j, i) => (
          <div key={`${j}-${i}`} className="py-2 text-xs sm:text-sm font-semibold">{j}</div>
        ))}
      </div>
      <div className="flex-1 flex flex-col min-w-[350px]">
        {weeks.map((week, weekIdx) => {
          const isCurrentMonth = week.some(d => d.getMonth() === month);
          if (!isCurrentMonth && weekIdx > 0) return null;
          const weekEvents = getEventsForWeek(week);
          const weekAgenda = getAgendaForWeek(week);
          return (
            <div key={`week-${weekIdx}-${week[0].getTime()}`} className="border-t flex flex-col">
              <div className="grid grid-cols-7">
                {week.map((day) => {
                  const isThisMonth = day.getMonth() === month;
                  const isToday = toDateStr(day) === todayStr;
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                  const dStr = toDateStr(day);
                  const jourFerie = isThisMonth ? isJourFerieCCQ(dStr) : null;
                  const congeCCQ = isThisMonth && !jourFerie ? isCongeCCQ(dStr) : null;
                  const bgClass = !isThisMonth
                    ? 'text-gray-300 bg-white'
                    : jourFerie
                      ? 'text-red-700 bg-red-50'
                      : congeCCQ
                        ? 'text-amber-700 bg-amber-50'
                        : isWeekend
                          ? 'text-gray-400 bg-gray-50'
                          : 'text-gray-700 bg-white';
                  return (
                    <div
                      key={dStr}
                      className={`px-2 py-1 text-[11px] flex justify-between items-center border-r border-b ${bgClass}`}
                      title={jourFerie ? jourFerie.nom : (congeCCQ ? congeCCQ.nom : '')}
                    >
                      <span className="truncate font-medium">
                        {jourFerie ? jourFerie.nom : congeCCQ ? 'Congé CCQ' : ''}
                      </span>
                      <span className={`font-medium text-xs ml-1 shrink-0 ${isToday ? 'bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center' : ''}`}>
                        {day.getDate()}
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* Événements agenda (RDV/invitations) — par-dessus les chantiers */}
              {weekAgenda.length > 0 && (
                <div className="px-1 space-y-0.5 pt-0.5">
                  {weekAgenda.map((ev) => {
                    const leftPct = (ev.dayIdx / 7) * 100;
                    const widthPct = (1 / 7) * 100;
                    return (
                      <div key={`agenda-${ev.id || ev._id || `${ev.date}-${ev.titre}`}`} className="relative h-5" style={{ marginLeft: `${leftPct}%`, width: `${widthPct}%` }}>
                        <div
                          className="w-full h-full rounded flex items-center px-1.5 overflow-hidden border-2 border-indigo-400"
                          style={{ backgroundColor: ev.couleur || '#6366f1' }}
                          title={`${ev.titre} (${ev.heure_debut || ''} - ${ev.heure_fin || ''})`}
                          data-testid={`agenda-event-${ev.id || ev._id}`}
                        >
                          <span className="text-white text-[9px] font-bold truncate leading-tight">
                            {ev.heure_debut || ''} {ev.titre}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Chantiers (bandes Gantt) — groupés en "lanes" (lignes) pour éviter les escaliers */}
              <div className="pb-1 space-y-1 min-h-[40px]">
                {packEventsIntoLanes(weekEvents).map((lane, laneIdx) => (
                  <div key={`lane-${laneIdx}`} className="relative h-7 w-full">
                    {lane.map((ev) => {
                      const spanCols = ev.lastIdx - ev.firstIdx + 1;
                      const leftPct = (ev.firstIdx / 7) * 100;
                      const widthPct = (spanCols / 7) * 100;
                      const color = eventColor(ev);
                      // 🆕 [v13.60.2] Libellé cohérent via helper (masque IDs techniques + nom client pour lavage)
                      const label = formatItemHoraire(ev);
                      const isClickable = ev.dossier_id || ev.route_id;
                      const handleClick = () => {
                        if (ev.route_id) {
                          navigate('/routes-lavage', { state: { selectedRouteId: ev.route_id } });
                        } else if (ev.dossier_id) {
                          navigate(`/crm/projets/${ev.dossier_id}?tab=bon-travail${ev.bon_id ? `&bonId=${ev.bon_id}` : ''}`);
                        }
                      };
                      return (
                        <div
                          key={`gantt-${ev.bon_id || ev.dossier_id || ev.route_id || ev.id || `${ev.event_type}-${leftPct}`}`}
                          className={`absolute top-0 h-full rounded flex items-center px-2 overflow-hidden ${isClickable ? 'cursor-pointer hover:brightness-110 transition-all' : ''}`}
                          style={{ left: `${leftPct}%`, width: `${widthPct}%`, backgroundColor: color, opacity: ev.event_type === 'horaire' ? 0.75 : 1 }}
                          onClick={isClickable ? handleClick : undefined}
                          title={label}
                          data-testid={`gantt-event-${ev.bon_id || ev.route_id || ev.id}`}
                        >
                          <span className="text-white text-[11px] font-semibold truncate leading-tight whitespace-nowrap">
                            {ev.event_type !== 'horaire' && (ev.type_travaux === 'interieur' ? '☁️ ' : ev.type_travaux === 'exterieur' ? '☀️ ' : '')}
                            {label}
                          </span>
                          {isClickable && <ExternalLink className="w-3 h-3 text-white/70 ml-1 shrink-0" />}
                        </div>
                      );
                    })}
                  </div>
                ))}
                {weekEvents.length === 0 && <div className="h-1" />}
              </div>
            </div>
          );
        })}
      </div>
      {/* Légende */}
      <div className="px-4 py-2 border-t bg-white flex flex-wrap gap-3 text-xs">
        <span className="text-gray-500 font-medium">Légende :</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: '#2563eb' }} />☁️ Intérieur</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: '#16a34a' }} />☀️ Extérieur</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: '#06b6d4' }} />Lavage</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: '#d97706' }} />Conge approuve</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0 border-2 border-indigo-400" style={{ backgroundColor: '#6366f1' }} />Evenement / RDV</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0 bg-red-100 border border-red-300" />Jour férié CCQ</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0 bg-amber-100 border border-amber-300" />Congé CCQ</span>
      </div>
    </div>
  );
}

// ======================== VUE ADMIN (RDV + Événements personnels) ========================
function VueAdminCalendrier({ token, user, year, month }) {
  const navigate = useNavigate();
  const [rdvs, setRdvs] = useState([]);
  const [agendaEvents, setAgendaEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [newEvent, setNewEvent] = useState({ titre: '', heure_debut: '09:00', heure_fin: '10:00', description: '', couleur: '#6366f1' });
  const [employees, setEmployees] = useState([]);
  const [selectedInvites, setSelectedInvites] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [showInvitDialog, setShowInvitDialog] = useState(false);
  const todayStr = toDateStr(new Date());

  // Cat H — dispatch UI : isAdmin détermine si l'utilisateur voit son propre calendrier (employé) OU peut consulter celui d'un autre employé (admins). Pas gate RBAC : les endpoints backend appliquent déjà l'ownership.
  // Déterminer le rôle admin AVANT loadData
  const isAdmin = estProprietaire(user) || aLeRole(user, 'charge_projet', 'adjoint_admin');

  const loadData = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    try {
      const employe_id = user?.employee_id || user?.id;
      const [rdvRes, agendaRes, empsRes, invitRes, lavageRes] = await Promise.all([
        axios.get(`${API_URL}/api/crm/rendez-vous?year=${year}&month=${month + 1}&employe_id=${employe_id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/agenda-perso?employe_id=${employe_id}&year=${year}&month=${month + 1}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/employees`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/agenda-perso/invitations?employe_id=${employe_id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/crm/routes-lavage`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { routes: [] } })),
      ]);
      const rdvData = rdvRes.data || { rendez_vous: [] };
      const agendaData = agendaRes.data || { events: [] };
      const empsData = empsRes.data || [];
      const invitData = invitRes.data || { invitations: [] };
      const allRoutes = lavageRes.data?.routes || [];

      // Filtrer les RDVs : peintres/chefs ne voient PAS les soumissions
      const allRdvs = rdvData.rendez_vous || [];
      const rdvsFiltres = isAdmin ? allRdvs : allRdvs.filter(r => r.type_rdv !== 'soumission');
      setRdvs(rdvsFiltres);

      // Routes de lavage où cet employé est assigné → ajouter comme événements
      const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
      const lavageEvents = allRoutes
        .filter(route => {
          const isAssigne = (route.employes || []).some(e => e.id === employe_id);
          const inMonth = (route.date_route || '').startsWith(monthStr);
          return isAssigne && inMonth && route.statut !== 'annule';
        })
        .map(route => ({
          id: `lavage-${route.id}`,
          titre: `🚿 Lavage — ${route.clients?.length || 0} client(s)`,
          heure: '07:00',
          couleur: '#06b6d4',
          type: 'lavage',
          date: route.date_route,
          clients: route.clients,
        }));
      setAgendaEvents([...(agendaData.events || []), ...lavageEvents]);
      setEmployees(Array.isArray(empsData) ? empsData : empsData.employees || []);
      setInvitations(invitData.invitations || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token, year, month, user, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDayClick = (day) => {
    if (day.getMonth() !== month) return;
    if (!isAdmin) return; // Les employés terrain ne peuvent pas créer d'événement
    setSelectedDate(toDateStr(day));
    setNewEvent({ titre: '', heure_debut: '09:00', heure_fin: '10:00', description: '', couleur: '#6366f1' });
    setSelectedInvites([]);
    setShowAddDialog(true);
  };

  const handleAddEvent = async () => {
    if (!newEvent.titre.trim()) { toast.error('Titre requis'); return; }
    const employe_id = user?.employee_id || user?.id;
    const res = await axios.post(`${API_URL}/api/agenda-perso`, { ...newEvent, employe_id, date: selectedDate, invites: selectedInvites });
    if (res.status === 200) {
      const evtCount = selectedInvites.length;
      toast.success(evtCount > 0 ? `Événement créé — ${evtCount} invitation(s) envoyée(s)` : 'Événement ajouté');
      setShowAddDialog(false);
      loadData();
    } else {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleRespondInvitation = async (eventId, reponse) => {
    const res = await axios.patch(`${API_URL}/api/agenda-perso/${eventId}/respond`, { reponse, employee_id: user?.employee_id || user?.id });
    if (res.status === 200) {
      toast.success(reponse === 'accepte' ? 'Invitation acceptée' : 'Invitation refusée');
      loadData();
    } else {
      toast.error('Erreur');
    }
  };

  const toggleInvite = (empId) => {
    setSelectedInvites(prev => prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]);
  };

  const handleDeleteEvent = async (eventId) => {
    const res = await axios.delete(`${API_URL}/api/agenda-perso/${eventId}`);
    if (res.status === 200) { toast.success('Événement supprimé'); loadData(); }
    else toast.error('Erreur suppression');
  };

  const weeks = getWeeks(year, month);

  const getEventsForDay = (dateStr) => {
    const myRdvs = rdvs.filter(r => r.date === dateStr).map(r => ({
      id: r.id, titre: r.titre || `RDV ${r.client_nom || ''}`, heure: r.heure_debut, couleur: '#0ea5e9', type: 'rdv', dossier_id: r.dossier_id,
    }));
    const myPerso = agendaEvents.filter(e => e.date === dateStr).map(e => ({
      id: e.id, titre: e.titre, heure: e.heure_debut, couleur: e.est_invitation ? '#a855f7' : (e.couleur || '#6366f1'), type: e.est_invitation ? 'invitation' : 'perso',
      invites: e.invites || [],
    }));
    return [...myRdvs, ...myPerso].sort((a, b) => (a.heure || '').localeCompare(b.heure || ''));
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-sm text-slate-400">Chargement...</div>;

  const dayEventsForSelectedDate = selectedDate ? getEventsForDay(selectedDate) : [];

  return (
    <div className="flex-1 flex flex-col overflow-x-auto">
      {/* En-tête jours */}
      <div className="grid grid-cols-7 bg-indigo-600 text-white text-center shrink-0 min-w-[350px]">
        {JOURS_HEADER.map((j, i) => (
          <div key={j.id || j._id || i} className="py-2 text-xs sm:text-sm font-semibold">{j}</div>
        ))}
      </div>
      <div className="flex-1 flex flex-col min-w-[350px]">
        {weeks.map((week, weekIdx) => {
          const isCurrentMonth = week.some(d => d.getMonth() === month);
          if (!isCurrentMonth && weekIdx > 0) return null;
          return (
            <div key={weekIdx} className="grid grid-cols-7 border-t" style={{ minHeight: '80px' }}>
              {week.map((day, dayIdx) => {
                const isThisMonth = day.getMonth() === month;
                const isToday = toDateStr(day) === todayStr;
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                const dateStr = toDateStr(day);
                const dayEvents = isThisMonth ? getEventsForDay(dateStr) : [];
                const jourFerie = isThisMonth ? isJourFerieCCQ(dateStr) : null;
                const congeCCQ = isThisMonth && !jourFerie ? isCongeCCQ(dateStr) : null;
                const ccqClass = jourFerie ? 'bg-red-50' : congeCCQ ? 'bg-amber-50' : '';
                return (
                  <div
                    key={dayIdx}
                    onClick={() => isThisMonth && handleDayClick(day)}
                    className={`border-r border-b p-1 flex flex-col ${isThisMonth ? 'cursor-pointer hover:bg-indigo-50 transition-colors' : 'bg-gray-50'} ${ccqClass || (isWeekend && isThisMonth ? 'bg-slate-50' : '')}`}
                    data-testid={isThisMonth ? `day-cell-${dateStr}` : undefined}
                    title={jourFerie ? jourFerie.nom : (congeCCQ ? congeCCQ.nom : '')}
                  >
                    <div className="flex justify-between items-start mb-1 gap-1">
                      <span className={`text-[10px] font-medium truncate ${jourFerie ? 'text-red-700' : congeCCQ ? 'text-amber-700' : 'text-transparent'}`}>
                        {jourFerie ? jourFerie.nom : congeCCQ ? 'Congé CCQ' : ''}
                      </span>
                      <span className={`text-xs font-medium shrink-0 ${!isThisMonth ? 'text-gray-300' : isToday ? 'bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center' : 'text-gray-700'}`}>
                        {day.getDate()}
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5 overflow-hidden">
                      {dayEvents.slice(0, 3).map((ev, i) => (
                        <div
                          key={ev.id || ev._id || i}
                          className="flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium text-white truncate"
                          style={{ backgroundColor: ev.couleur }}
                          onClick={(e) => { e.stopPropagation(); ev.type === 'rdv' && ev.dossier_id && navigate(`/crm/projets/${ev.dossier_id}`); }}
                          title={`${ev.heure || ''} ${ev.titre}`}
                        >
                          {ev.heure && <span className="opacity-75 shrink-0">{ev.heure}</span>}
                          <span className="truncate">{ev.titre}</span>
                        </div>
                      ))}
                      {dayEvents.length > 3 && <span className="text-[9px] text-indigo-600 font-medium">+{dayEvents.length - 3}</span>}
                      {isThisMonth && dayEvents.length === 0 && (
                        <div className="flex items-center justify-center opacity-0 hover:opacity-30 transition-opacity">
                          <Plus className="w-3 h-3 text-indigo-400" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Légende */}
      <div className="px-4 py-2 border-t bg-white flex flex-wrap gap-3 text-xs items-center">
        <span className="text-gray-500 font-medium">Légende :</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0 bg-sky-500" />RDV client</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0 bg-indigo-500" />Personnel</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0 bg-purple-500" />Invitation acceptée</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0 bg-red-100 border border-red-300" />Jour férié CCQ</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0 bg-amber-100 border border-amber-300" />Congé CCQ</span>
        {invitations.length > 0 && (
          <button onClick={() => setShowInvitDialog(true)} className="ml-auto flex items-center gap-1.5 text-amber-600 hover:text-amber-700 font-semibold" data-testid="btn-invitations-pending">
            <Bell className="w-4 h-4" />
            {invitations.length} invitation(s) en attente
          </button>
        )}
        {invitations.length === 0 && (
          <span className="ml-auto text-indigo-500 font-medium cursor-pointer">Cliquez sur un jour pour ajouter</span>
        )}
      </div>

      {/* Dialog ajouter événement */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-indigo-600" />
                Ajouter un événement
                {selectedDate && <span className="text-sm font-normal text-gray-500">— {new Date(selectedDate + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' })}</span>}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {/* Événements existants ce jour */}
            {dayEventsForSelectedDate.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Événements ce jour</p>
                {dayEventsForSelectedDate.map((ev) => (
                  <div key={ev.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: ev.couleur }} />
                      <span className="truncate">{ev.heure} — {ev.titre}</span>
                      <Badge className="text-[9px] px-1 py-0 shrink-0" style={{ backgroundColor: ev.type === 'rdv' ? '#e0f2fe' : '#e0e7ff', color: ev.type === 'rdv' ? '#0369a1' : '#4338ca' }}>
                        {ev.type === 'rdv' ? 'RDV' : 'Perso'}
                      </Badge>
                    </div>
                    {ev.type === 'perso' && (
                      <button onClick={() => handleDeleteEvent(ev.id)} className="text-red-400 hover:text-red-600 ml-2 shrink-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {ev.type === 'invitation' && (
                      <Badge className="text-[9px] px-1 py-0 shrink-0 bg-purple-100 text-purple-700 border-purple-300">Invitation</Badge>
                    )}
                    {ev.invites?.length > 0 && ev.type === 'perso' && (
                      <span className="text-xs text-indigo-400 ml-1 shrink-0 flex items-center gap-0.5"><Users className="w-3 h-3" />{ev.invites.length}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {/* Formulaire nouvel événement */}
            <div>
              <Label className="text-xs">Titre *</Label>
              <Input value={newEvent.titre} onChange={e => setNewEvent(v => ({ ...v, titre: e.target.value }))} placeholder="Ex: Réunion d'équipe, Appel fournisseur..." className="mt-0.5" data-testid="agenda-titre" />
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <Label className="text-xs">Début</Label>
                <Input type="time" value={newEvent.heure_debut} onChange={e => setNewEvent(v => ({ ...v, heure_debut: e.target.value }))} className="mt-0.5" />
              </div>
              <div className="flex-1">
                <Label className="text-xs">Fin</Label>
                <Input type="time" value={newEvent.heure_fin} onChange={e => setNewEvent(v => ({ ...v, heure_fin: e.target.value }))} className="mt-0.5" />
              </div>
              <div>
                <Label className="text-xs">Couleur</Label>
                <input type="color" value={newEvent.couleur} onChange={e => setNewEvent(v => ({ ...v, couleur: e.target.value }))} className="mt-0.5 h-9 w-10 rounded border cursor-pointer" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Note (optionnel)</Label>
              <Input value={newEvent.description} onChange={e => setNewEvent(v => ({ ...v, description: e.target.value }))} placeholder="Note additionnelle..." className="mt-0.5" />
            </div>
            {/* Inviter des employés */}
            <div>
              <Label className="text-xs flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Inviter des employés (optionnel)</Label>
              <div className="mt-1 max-h-32 overflow-y-auto border rounded-lg divide-y" data-testid="invite-employees-list">
                {employees.filter(e => e.id !== (user?.employee_id || user?.id)).map(emp => (
                  <label key={emp.id} className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-indigo-50 transition text-sm ${selectedInvites.includes(emp.id) ? 'bg-indigo-50' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedInvites.includes(emp.id)}
                      onChange={() => toggleInvite(emp.id)}
                      className="rounded border-slate-300"
                    />
                    <span>{emp.prenom} {emp.nom}</span>
                    {emp.poste && <span className="text-xs text-slate-400 ml-auto">{emp.poste}</span>}
                  </label>
                ))}
                {employees.filter(e => e.id !== (user?.employee_id || user?.id)).length === 0 && (
                  <div className="text-xs text-slate-400 p-3 text-center">Aucun employé disponible</div>
                )}
              </div>
              {selectedInvites.length > 0 && (
                <p className="text-xs text-indigo-600 mt-1">{selectedInvites.length} employé(s) invité(s) — un courriel sera envoyé</p>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
            <Button onClick={handleAddEvent} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-ajouter-agenda">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Invitations en attente */}
      <Dialog open={showInvitDialog} onOpenChange={setShowInvitDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-amber-500" /> Invitations en attente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-80 overflow-y-auto" data-testid="invitations-pending-list">
            {invitations.map(ev => (
              <div key={ev.id} className="border rounded-lg p-3 bg-amber-50 border-amber-200">
                <div className="font-semibold text-sm text-slate-800">{ev.titre}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {ev.date} | {ev.heure_debut} — {ev.heure_fin}
                </div>
                {ev.created_by_nom && (
                  <div className="text-xs text-slate-500 mt-0.5">Organisé par : <strong>{ev.created_by_nom}</strong></div>
                )}
                {ev.description && (
                  <div className="text-xs text-slate-600 mt-1">{ev.description}</div>
                )}
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="default" className="bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => { handleRespondInvitation(ev.id, 'accepte'); }} data-testid={`accept-invite-${ev.id}`}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Accepter
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-300 hover:bg-red-50" onClick={() => { handleRespondInvitation(ev.id, 'refuse'); }} data-testid={`decline-invite-${ev.id}`}>
                    <X className="w-3.5 h-3.5 mr-1" /> Refuser
                  </Button>
                </div>
              </div>
            ))}
            {invitations.length === 0 && (
              <div className="text-center text-slate-400 text-sm py-6">Aucune invitation en attente</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ======================== VUE SEMAINE TERRAIN ========================
function VueSemaineTerrain({ token, user, year, month, weekOffset, setWeekOffset }) {
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);

  // Calculate the week dates based on offset from current week
  const getWeekDates = () => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1 + (weekOffset * 7)); // Monday of target week
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      days.push(d);
    }
    return days;
  };

  const weekDays = getWeekDates();
  const weekStart = weekDays[0];
  const weekEnd = weekDays[6];
  const targetMonth = weekStart.getMonth() + 1;
  const targetYear = weekStart.getFullYear();

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    // Fetch the month that contains the week start
    axios.get(`${API_URL}/api/employees/my-schedule-month?year=${targetYear}&month=${targetMonth}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.data || { schedule: [] })
      .then(d => setSchedule(d.schedule || []))
      .catch(() => setSchedule([]))
      .finally(() => setLoading(false));
  }, [token, targetYear, targetMonth]);

  const todayStr = toDateStr(new Date());
  const JOURS_COMPLETS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  // Get events for a specific date
  const getEventsForDate = (dateStr) => {
    return schedule.filter(ev => (ev.dates || []).includes(dateStr));
  };

  const eventColor = (ev) => {
    if (ev.event_type === 'conge') return '#d97706';
    if (ev.event_type === 'lavage') return '#06b6d4';
    if (ev.event_type === 'horaire') return '#4f46e5';
    if (ev.event_type === 'bt_interne' || ev.est_interne) return '#ea580c';
    if (ev.type_travaux === 'exterieur') return '#16a34a';
    return '#2563eb';
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-sm text-slate-400">Chargement...</div>;

  return (
    <div className="flex-1 flex flex-col">
      {/* Navigation semaine */}
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b">
        <Button variant="ghost" size="sm" onClick={() => setWeekOffset(o => o - 1)}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm font-medium text-slate-700">
          {weekStart.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' })} — {weekEnd.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="text-xs px-2" onClick={() => setWeekOffset(0)} disabled={weekOffset === 0}>
            Cette semaine
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setWeekOffset(o => o + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {/* Grille semaine */}
      <div className="flex-1 grid grid-cols-7 divide-x">
        {weekDays.map((day, i) => {
          const dateStr = toDateStr(day);
          const isToday = dateStr === todayStr;
          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
          const events = getEventsForDate(dateStr);
          const jourFerie = isJourFerieCCQ(dateStr);
          const congeCCQ = !jourFerie ? isCongeCCQ(dateStr) : null;
          const dayBg = jourFerie ? 'bg-red-50' : congeCCQ ? 'bg-amber-50' : isWeekend ? 'bg-slate-50' : 'bg-white';
          return (
            <div key={i} className={`flex flex-col min-h-[120px] ${dayBg}`}>
              <div className={`px-2 py-1.5 text-center border-b ${isToday ? 'bg-blue-600 text-white' : jourFerie ? 'bg-red-100 text-red-800' : congeCCQ ? 'bg-amber-100 text-amber-800' : 'bg-slate-100'}`} title={jourFerie ? jourFerie.nom : (congeCCQ ? congeCCQ.nom : '')}>
                <div className="text-[10px] font-medium uppercase">{JOURS_COMPLETS[day.getDay()]}</div>
                <div className={`text-lg font-bold ${isToday ? '' : jourFerie ? 'text-red-800' : congeCCQ ? 'text-amber-800' : 'text-slate-700'}`}>{day.getDate()}</div>
                {(jourFerie || congeCCQ) && (
                  <div className="text-[9px] font-medium leading-tight truncate mt-0.5">
                    {jourFerie ? jourFerie.nom : 'Congé CCQ'}
                  </div>
                )}
              </div>
              <div className="flex-1 p-1 space-y-1 overflow-y-auto">
                {events.map((ev, j) => {
                  const color = eventColor(ev);
                  // 🆕 [v13.60.2] Libellé cohérent
                  const label = formatItemHoraire(ev);
                  const isClickable = ev.dossier_id || ev.route_id;
                  const handleClick = () => {
                    if (ev.route_id) {
                      navigate('/routes-lavage', { state: { selectedRouteId: ev.route_id } });
                    } else if (ev.dossier_id) {
                      navigate(`/crm/projets/${ev.dossier_id}?tab=bon-travail${ev.bon_id ? `&bonId=${ev.bon_id}` : ''}`);
                    }
                  };
                  return (
                    <div
                      key={j}
                      className={`rounded px-1.5 py-1 text-white text-[10px] leading-tight ${isClickable ? 'cursor-pointer hover:brightness-110' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={isClickable ? handleClick : undefined}
                      data-testid={`week-event-${i}-${j}`}
                    >
                      <div className="font-semibold truncate">
                        {ev.event_type !== 'horaire' && (ev.type_travaux === 'interieur' ? '☁️ ' : ev.type_travaux === 'exterieur' ? '☀️ ' : '')}
                        {label}
                      </div>
                      {ev.adresse && <div className="opacity-80 truncate">{ev.adresse}</div>}
                    </div>
                  );
                })}
                {events.length === 0 && !isWeekend && (
                  <div className="text-[10px] text-slate-300 text-center pt-4">—</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Légende */}
      <div className="px-4 py-2 border-t bg-white flex flex-wrap gap-3 text-xs">
        <span className="text-gray-500 font-medium">Légende :</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: '#2563eb' }} />☁️ Intérieur</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: '#16a34a' }} />☀️ Extérieur</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: '#06b6d4' }} />Lavage</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: '#d97706' }} />Congé approuvé</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0 bg-red-100 border border-red-300" />Jour férié CCQ</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm shrink-0 bg-amber-100 border border-amber-300" />Congé CCQ</span>
      </div>
    </div>
  );
}

// ======================== PAGE PRINCIPALE ========================
export default function CalendrierProductionEmploye() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [monthOffset, setMonthOffset] = useState(0);
  const [weekOffset, setWeekOffset] = useState(0);
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'week'

  const isAdmin = user?.role && ADMIN_ROLES.includes(user.role);

  const monthStart = (() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + monthOffset);
    return d;
  })();
  const year = monthStart.getFullYear();
  const month = monthStart.getMonth();

  return (
    <div className="p-2 sm:p-4 flex flex-col" style={{ minHeight: 'calc(100vh - 80px)' }}>
      {/* En-tête */}
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center mb-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/mon-espace')} className="text-gray-500">
            ← Mon Espace
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {isAdmin ? 'Mon Agenda' : 'Calendrier de Production'}
            </h1>
            <p className="text-xs text-gray-500">
              {isAdmin ? 'Rendez-vous clients et événements personnels' : 'Vos chantiers planifiés'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isAdmin && (
            <div className="flex bg-slate-100 rounded-lg p-0.5" data-testid="view-toggle">
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${viewMode === 'month' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="view-month-btn"
              >
                Mois
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${viewMode === 'week' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="view-week-btn"
              >
                Semaine
              </button>
            </div>
          )}
          {isAdmin && (
            <Badge className="bg-indigo-100 text-indigo-700">
              Cliquez sur un jour pour ajouter un événement
            </Badge>
          )}
        </div>
      </div>

      <div className="flex-1 border rounded-lg overflow-hidden flex flex-col bg-white">
        {/* Navigation mois (visible en vue mois ou admin) */}
        {(viewMode === 'month' || isAdmin) && (
          <div className="flex items-center justify-between px-4 py-2 bg-white border-b">
            <Button variant="ghost" size="sm" onClick={() => setMonthOffset(o => o - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <h2 className="text-lg font-bold">{MOIS_FR[month]} {year}</h2>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="text-xs px-2" onClick={() => setMonthOffset(0)} disabled={monthOffset === 0}>
                Aujourd'hui
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setMonthOffset(o => o + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {isAdmin
          ? <VueAdminCalendrier token={token} user={user} year={year} month={month} />
          : viewMode === 'week'
            ? <VueSemaineTerrain token={token} user={user} year={year} month={month} weekOffset={weekOffset} setWeekOffset={setWeekOffset} />
            : <VueGanttTerrain token={token} user={user} year={year} month={month} />
        }
      </div>
    </div>
  );
}
